
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { colorSchemeStyles } from '@/components/premium-bots/utils/styles';
import { PropBotCardHeader } from './card/PropBotCardHeader';
import { PropBotStatsSection } from './card/PropBotStatsSection';
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePropBotMultilingualContent } from "@/hooks/use-prop-bot-multilingual-content";
import { EnhancedPropBot } from "@/hooks/use-enhanced-prop-bots";

interface PropBotCardProps {
  bot: EnhancedPropBot;
  onAction: () => void;
}

export const PropBotCard = ({ bot, onAction }: PropBotCardProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { language } = useLanguage();
  
  // OPTIMIZATION: Lazy load i18n content (fetch only on hover)
  const { multilingualContent, isLoading, enableFetching } = usePropBotMultilingualContent(bot, language);
  
  const {
    name,
    is_integrated,
    color_scheme,
    propDetails
  } = bot;
  
  // Extract propDetails safely (handle both array and object formats)
  const propDetailsObj = Array.isArray(propDetails) && propDetails.length > 0 
    ? propDetails[0] 
    : propDetails;

  const propFirm = propDetailsObj?.prop_firm;
  const passRate = propDetailsObj?.pass_rate;
  const estimatedPassTime = propDetailsObj?.estimated_pass_time;
  const connectedAccountsTotal = propDetailsObj?.connected_accounts_total;
  const isInstantAccount = propDetailsObj?.is_instant_account || false;
  const winRate = propDetailsObj?.winrate;
  const potentialProfit = propDetailsObj?.potential_profit;
  
  const cardStyle = color_scheme ? colorSchemeStyles[color_scheme] : '';

  return (
    <div
      className={cn(
        'rounded-lg card-shine transition-all duration-300 hover:translate-y-[-2px] animate-fade-in',
        cardStyle,
        isInstantAccount && 'bg-gradient-to-br from-amber-500/5 to-yellow-600/5 border-amber-500/20'
      )}
      onMouseEnter={enableFetching}
      data-tour="prop-bot-card"
    >
      <Card className="border-0 bg-transparent h-full flex flex-col">
        <PropBotCardHeader 
          name={name}
          description={multilingualContent.description}
          propFirm={propFirm || t('unknown')}
          colorScheme={color_scheme}
          isLoading={isLoading}
          isInstantAccount={isInstantAccount}
        />
        
        <PropBotStatsSection 
          passRate={passRate}
          estimatedPassTime={estimatedPassTime}
          connectedAccountsTotal={connectedAccountsTotal}
          isInstantAccount={isInstantAccount}
          winRate={winRate}
          potentialProfit={potentialProfit}
          enhancedStats={bot.enhancedStats}
        />
        
        <div className={`${isMobile ? "px-4 pb-4" : "px-6 pb-6"}`}>
          {is_integrated ? (
            <Button 
              onClick={onAction} 
              className="w-full" 
              variant="outline"
              size={isMobile ? "sm" : "default"}
              data-tour="start-challenge-btn"
            >
              <Check className={`${isMobile ? "h-3.5 w-3.5" : "mr-2 h-4 w-4"}`} /> {t('manage')}
            </Button>
          ) : (
            <Button 
              onClick={onAction} 
              className={cn(
                "w-full",
                isInstantAccount && "bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white border-0"
              )}
              variant={isInstantAccount ? "default" : "default"}
              size={isMobile ? "sm" : "default"}
              data-tour="start-challenge-btn"
            >
              {isInstantAccount ? t('start_instant_trading') : t('start_challenge')}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
